/**
 * API Endpoints Documentation Page
 * 
 * Comprehensive list of all API endpoints available in the admin dashboard.
 * Organized by category for easy reference.
 */

import { motion } from 'framer-motion';
import { 
  Code2, 
  Search,
  Shield,
  User,
  Key,
  Server,
  Zap,
  Cloud,
  Network,
  Activity,
  Database,
  FileCode,
  Terminal,
  Settings
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  description: string;
  auth?: string;
}

interface EndpointCategory {
  name: string;
  icon: any;
  color: string;
  bgGradient: string;
  endpoints: Endpoint[];
}

const categories: EndpointCategory[] = [
  {
    name: 'Health & Status',
    icon: Activity,
    color: 'text-emerald-400',
    bgGradient: 'from-emerald-500/10 to-teal-600/5',
    endpoints: [
      { method: 'GET', path: '/health', description: 'Health check endpoint' },
      { method: 'GET', path: '/ready', description: 'Readiness probe' },
      { method: 'GET', path: '/live', description: 'Liveness probe' },
    ],
  },
  {
    name: 'Authentication',
    icon: Shield,
    color: 'text-blue-400',
    bgGradient: 'from-blue-500/10 to-indigo-600/5',
    endpoints: [
      { method: 'POST', path: '/auth/login', description: 'Login to the platform' },
      { method: 'POST', path: '/auth/register', description: 'Create new user account' },
      { method: 'POST', path: '/auth/activate', description: 'Activate user account' },
      { method: 'POST', path: '/auth/resend-activation', description: 'Resend activation email' },
      { method: 'GET', path: '/auth/users', description: 'List all users', auth: 'Admin' },
      { method: 'POST', path: '/auth/create-admin', description: 'Create admin user', auth: 'Admin' },
      { method: 'DELETE', path: '/auth/users/:userId', description: 'Delete user account', auth: 'Admin' },
      { method: 'PATCH', path: '/auth/users/:userId/suspend', description: 'Suspend user account', auth: 'Admin' },
      { method: 'PATCH', path: '/auth/users/:userId/unsuspend', description: 'Unsuspend user account', auth: 'Admin' },
      { method: 'POST', path: '/auth/users/:userId/reset-password', description: 'Reset user password', auth: 'Admin' },
    ],
  },
  {
    name: 'Profile Management',
    icon: User,
    color: 'text-violet-400',
    bgGradient: 'from-violet-500/10 to-purple-600/5',
    endpoints: [
      { method: 'GET', path: '/profile', description: 'Get current user profile', auth: 'JWT' },
      { method: 'PATCH', path: '/profile', description: 'Update user profile', auth: 'JWT' },
      { method: 'POST', path: '/profile/avatar', description: 'Upload profile avatar', auth: 'JWT' },
      { method: 'DELETE', path: '/profile/avatar', description: 'Delete profile avatar', auth: 'JWT' },
      { method: 'POST', path: '/profile/password', description: 'Change password', auth: 'JWT' },
      { method: 'GET', path: '/profile/notifications', description: 'Get notification preferences', auth: 'JWT' },
      { method: 'PATCH', path: '/profile/notifications', description: 'Update notification preferences', auth: 'JWT' },
      { method: 'GET', path: '/profile/usage', description: 'Get usage statistics', auth: 'JWT' },
      { method: 'POST', path: '/profile/export', description: 'Export user data', auth: 'JWT' },
      { method: 'DELETE', path: '/profile', description: 'Delete account', auth: 'JWT' },
      { method: 'GET', path: '/profile/2fa/status', description: 'Get 2FA status', auth: 'JWT' },
      { method: 'POST', path: '/profile/2fa/setup', description: 'Setup 2FA', auth: 'JWT' },
      { method: 'POST', path: '/profile/2fa/verify', description: 'Verify 2FA code', auth: 'JWT' },
      { method: 'POST', path: '/profile/2fa/disable', description: 'Disable 2FA', auth: 'JWT' },
      { method: 'POST', path: '/profile/2fa/recovery-codes', description: 'Generate recovery codes', auth: 'JWT' },
      { method: 'GET', path: '/profile/sessions', description: 'List active sessions', auth: 'JWT' },
      { method: 'DELETE', path: '/profile/sessions/:sessionId', description: 'Delete session', auth: 'JWT' },
      { method: 'POST', path: '/profile/sessions/revoke-all', description: 'Revoke all sessions', auth: 'JWT' },
      { method: 'GET', path: '/profile/login-history', description: 'Get login history', auth: 'JWT' },
      { method: 'POST', path: '/profile/logout', description: 'Logout current session', auth: 'JWT' },
    ],
  },
  {
    name: 'API Keys',
    icon: Key,
    color: 'text-amber-400',
    bgGradient: 'from-amber-500/10 to-orange-600/5',
    endpoints: [
      { method: 'POST', path: '/api-keys', description: 'Create new API key', auth: 'JWT' },
      { method: 'GET', path: '/api-keys', description: 'List all API keys', auth: 'JWT' },
      { method: 'DELETE', path: '/api-keys/:id', description: 'Delete API key', auth: 'JWT' },
    ],
  },
  {
    name: 'GPU Jobs',
    icon: Zap,
    color: 'text-gpu-400',
    bgGradient: 'from-gpu-500/10 to-teal-600/5',
    endpoints: [
      { method: 'POST', path: '/gpu-jobs', description: 'Create new GPU job', auth: 'API Key' },
      { method: 'GET', path: '/gpu-jobs', description: 'List all GPU jobs', auth: 'API Key' },
      { method: 'GET', path: '/gpu-jobs/instances', description: 'List running instances', auth: 'API Key' },
      { method: 'GET', path: '/gpu-jobs/estimate/cost', description: 'Estimate job cost', auth: 'API Key' },
      { method: 'GET', path: '/gpu-jobs/:jobId', description: 'Get job details', auth: 'API Key' },
      { method: 'GET', path: '/gpu-jobs/:jobId/network', description: 'Get job network info', auth: 'API Key' },
      { method: 'DELETE', path: '/gpu-jobs/:jobId', description: 'Cancel GPU job', auth: 'API Key' },
      { method: 'DELETE', path: '/gpu-jobs/:jobId/purge', description: 'Purge job completely', auth: 'API Key' },
    ],
  },
  {
    name: 'Infrastructure',
    icon: Server,
    color: 'text-rose-400',
    bgGradient: 'from-rose-500/10 to-pink-600/5',
    endpoints: [
      { method: 'GET', path: '/infrastructure/status', description: 'Get infrastructure status' },
      { method: 'POST', path: '/infrastructure/provision', description: 'Provision infrastructure' },
      { method: 'DELETE', path: '/infrastructure/destroy', description: 'Destroy infrastructure' },
      { method: 'GET', path: '/infrastructure/deployments', description: 'List deployments' },
      { method: 'GET', path: '/infrastructure/deployments/list', description: 'List all deployments' },
      { method: 'GET', path: '/infrastructure/deployments/:id', description: 'Get deployment details' },
      { method: 'POST', path: '/infrastructure/deploy/ec2', description: 'Deploy EC2 infrastructure' },
      { method: 'POST', path: '/infrastructure/deploy/ecs', description: 'Deploy ECS infrastructure' },
      { method: 'POST', path: '/infrastructure/destroy/ec2', description: 'Destroy EC2 infrastructure' },
      { method: 'POST', path: '/infrastructure/destroy/ecs', description: 'Destroy ECS infrastructure' },
      { method: 'POST', path: '/infrastructure/cancel/ec2', description: 'Cancel EC2 deployment' },
      { method: 'POST', path: '/infrastructure/cancel/ecs', description: 'Cancel ECS deployment' },
      { method: 'GET', path: '/infrastructure/fleet/status', description: 'Get fleet status' },
      { method: 'GET', path: '/infrastructure/cost-savings', description: 'Get cost savings report' },
      { method: 'POST', path: '/infrastructure/fleet/start', description: 'Start fleet instances' },
      { method: 'POST', path: '/infrastructure/fleet/stop', description: 'Stop fleet instances' },
      { method: 'POST', path: '/infrastructure/fleet/terminate', description: 'Terminate fleet instances' },
      { method: 'POST', path: '/infrastructure/instances/:instanceId/start', description: 'Start instance' },
      { method: 'POST', path: '/infrastructure/instances/:instanceId/stop', description: 'Stop instance' },
      { method: 'POST', path: '/infrastructure/instances/:instanceId/hibernate', description: 'Hibernate instance' },
      { method: 'POST', path: '/infrastructure/instances/:instanceId/terminate', description: 'Terminate instance' },
    ],
  },
  {
    name: 'ECS Management',
    icon: Database,
    color: 'text-cyan-400',
    bgGradient: 'from-cyan-500/10 to-blue-600/5',
    endpoints: [
      { method: 'GET', path: '/infrastructure/ecs/clusters', description: 'List ECS clusters' },
      { method: 'GET', path: '/infrastructure/ecs/clusters/:clusterName', description: 'Get cluster details' },
      { method: 'GET', path: '/infrastructure/ecs/clusters/:clusterName/tasks', description: 'List cluster tasks' },
      { method: 'GET', path: '/infrastructure/ecs/clusters/:clusterName/container-instances', description: 'List container instances' },
      { method: 'GET', path: '/infrastructure/ecs/clusters/:clusterName/tasks/history', description: 'Get task history', auth: 'JWT/API Key' },
      { method: 'GET', path: '/infrastructure/ecs/clusters/:clusterName/tasks/:taskId/details', description: 'Get task details', auth: 'JWT/API Key' },
      { method: 'GET', path: '/infrastructure/ecs/clusters/:clusterName/services', description: 'List cluster services' },
      { method: 'POST', path: '/infrastructure/ecs/clusters/:clusterName/scale', description: 'Scale cluster', auth: 'JWT/API Key' },
      { method: 'POST', path: '/infrastructure/ecs/clusters/:clusterName/repair', description: 'Repair cluster' },
      { method: 'POST', path: '/infrastructure/ecs/clusters/:clusterName/update-task-definition', description: 'Update task definition', auth: 'JWT/API Key' },
      { method: 'POST', path: '/infrastructure/ecs/clusters/:clusterName/tasks', description: 'Create ECS task', auth: 'JWT/API Key' },
      { method: 'POST', path: '/infrastructure/ecs/tasks/stop', description: 'Stop ECS task', auth: 'JWT/API Key' },
      { method: 'GET', path: '/infrastructure/ecs/clusters/:clusterName/tasks/:taskId/logs', description: 'Get task logs', auth: 'JWT/API Key' },
      { method: 'GET', path: '/infrastructure/ecs/clusters/:clusterName/logs', description: 'Get cluster logs', auth: 'JWT/API Key' },
      { method: 'POST', path: '/infrastructure/ecs/clusters/:clusterName/instances/:instanceId/terminate', description: 'Terminate container instance', auth: 'JWT/API Key' },
      { method: 'POST', path: '/infrastructure/ecs/clusters/:clusterName/instances/terminate-bulk', description: 'Terminate multiple instances', auth: 'JWT/API Key' },
      { method: 'DELETE', path: '/infrastructure/ecs/clusters/:clusterName', description: 'Delete ECS cluster', auth: 'JWT/API Key' },
    ],
  },
  {
    name: 'Network Management',
    icon: Network,
    color: 'text-indigo-400',
    bgGradient: 'from-indigo-500/10 to-blue-600/5',
    endpoints: [
      { method: 'GET', path: '/network/detailed', description: 'Get detailed network info' },
      { method: 'POST', path: '/network/security-groups/:groupId/rules', description: 'Add security group rule' },
      { method: 'DELETE', path: '/network/security-groups/:groupId/rules', description: 'Remove security group rule' },
      { method: 'PATCH', path: '/network/resources/:resourceId/tags', description: 'Update resource tags' },
      { method: 'POST', path: '/infrastructure/network/cleanup/:vpcId', description: 'Cleanup VPC resources' },
      { method: 'POST', path: '/infrastructure/network/cleanup-orphaned', description: 'Cleanup orphaned resources' },
    ],
  },
  {
    name: 'Serverless Functions',
    icon: Cloud,
    color: 'text-purple-400',
    bgGradient: 'from-purple-500/10 to-fuchsia-600/5',
    endpoints: [
      { method: 'POST', path: '/functions', description: 'Create serverless function', auth: 'API Key' },
      { method: 'GET', path: '/functions', description: 'List serverless functions', auth: 'API Key' },
      { method: 'GET', path: '/functions/:id', description: 'Get function details', auth: 'API Key' },
      { method: 'PUT', path: '/functions/:id', description: 'Update serverless function', auth: 'API Key' },
      { method: 'DELETE', path: '/functions/:id', description: 'Delete serverless function', auth: 'API Key' },
      { method: 'POST', path: '/functions/:id/build', description: 'Build function', auth: 'API Key' },
      { method: 'POST', path: '/functions/:id/invoke', description: 'Invoke function', auth: 'API Key' },
    ],
  },
  {
    name: 'Invocations',
    icon: FileCode,
    color: 'text-teal-400',
    bgGradient: 'from-teal-500/10 to-emerald-600/5',
    endpoints: [
      { method: 'GET', path: '/invocations', description: 'List function invocations', auth: 'API Key' },
      { method: 'GET', path: '/invocations/:id', description: 'Get invocation details', auth: 'API Key' },
      { method: 'GET', path: '/invocations/:id/result', description: 'Get invocation result', auth: 'API Key' },
      { method: 'GET', path: '/invocations/:id/logs', description: 'Get invocation logs', auth: 'API Key' },
      { method: 'POST', path: '/invocations/:id/cancel', description: 'Cancel invocation', auth: 'API Key' },
    ],
  },
  {
    name: 'Workers',
    icon: Terminal,
    color: 'text-orange-400',
    bgGradient: 'from-orange-500/10 to-red-600/5',
    endpoints: [
      { method: 'POST', path: '/workers/register', description: 'Register new worker', auth: 'API Key' },
      { method: 'POST', path: '/workers/heartbeat', description: 'Worker heartbeat', auth: 'API Key' },
      { method: 'GET', path: '/workers/jobs/next', description: 'Get next queued job', auth: 'API Key' },
      { method: 'POST', path: '/workers/jobs/:invocationId/start', description: 'Start job execution', auth: 'API Key' },
      { method: 'POST', path: '/workers/jobs/:invocationId/complete', description: 'Complete job execution', auth: 'API Key' },
      { method: 'POST', path: '/workers/jobs/:invocationId/fail', description: 'Fail job execution', auth: 'API Key' },
      { method: 'POST', path: '/workers/jobs/:invocationId/logs', description: 'Send job logs', auth: 'API Key' },
    ],
  },
  {
    name: 'Operations',
    icon: Activity,
    color: 'text-green-400',
    bgGradient: 'from-green-500/10 to-emerald-600/5',
    endpoints: [
      { method: 'GET', path: '/operations', description: 'List operations', auth: 'JWT' },
      { method: 'GET', path: '/operations/active', description: 'Get active operations', auth: 'JWT' },
      { method: 'GET', path: '/operations/attention', description: 'Get items needing attention', auth: 'JWT' },
      { method: 'GET', path: '/operations/stats', description: 'Get operation statistics', auth: 'JWT' },
      { method: 'GET', path: '/operations/search', description: 'Search operations', auth: 'JWT' },
      { method: 'GET', path: '/operations/correlation/:correlationId', description: 'Get correlated operations', auth: 'JWT' },
      { method: 'GET', path: '/operations/:id', description: 'Get operation details', auth: 'JWT' },
      { method: 'GET', path: '/operations/:id/steps', description: 'Get operation steps', auth: 'JWT' },
      { method: 'GET', path: '/operations/:id/events', description: 'Get operation events', auth: 'JWT' },
      { method: 'GET', path: '/operations/:id/timeline', description: 'Get operation timeline', auth: 'JWT' },
      { method: 'POST', path: '/operations/:id/cancel', description: 'Cancel operation', auth: 'JWT' },
      { method: 'POST', path: '/operations/:id/retry', description: 'Retry operation', auth: 'JWT' },
      { method: 'POST', path: '/operations/:id/acknowledge', description: 'Acknowledge operation', auth: 'JWT' },
    ],
  },
  {
    name: 'Executions',
    icon: Settings,
    color: 'text-slate-400',
    bgGradient: 'from-slate-500/10 to-gray-600/5',
    endpoints: [
      { method: 'GET', path: '/executions', description: 'List executions', auth: 'JWT' },
      { method: 'GET', path: '/executions/recent', description: 'Get recent executions', auth: 'JWT' },
      { method: 'GET', path: '/executions/stats', description: 'Get execution statistics', auth: 'JWT' },
      { method: 'GET', path: '/executions/:id', description: 'Get execution details', auth: 'JWT' },
      { method: 'POST', path: '/executions/:id/cancel', description: 'Cancel execution', auth: 'JWT' },
      { method: 'DELETE', path: '/executions/:id', description: 'Delete execution', auth: 'JWT' },
    ],
  },
  {
    name: 'Activity Logs',
    icon: FileCode,
    color: 'text-yellow-400',
    bgGradient: 'from-yellow-500/10 to-amber-600/5',
    endpoints: [
      { method: 'GET', path: '/activity', description: 'Get recent activity logs', auth: 'JWT' },
    ],
  },
];

const getMethodColor = (method: string) => {
  const colors = {
    GET: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
    POST: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
    PUT: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
    DELETE: 'bg-red-500/15 text-red-400 border-red-500/25',
    PATCH: 'bg-violet-500/15 text-violet-400 border-violet-500/25',
  };
  return colors[method as keyof typeof colors] || 'bg-surface-500/15 text-surface-400 border-surface-500/25';
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export default function EndpointsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Filter categories and endpoints based on search
  const filteredCategories = categories.map(category => ({
    ...category,
    endpoints: category.endpoints.filter(endpoint =>
      endpoint.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
      endpoint.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      endpoint.method.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter(category => 
    selectedCategory ? category.name === selectedCategory : category.endpoints.length > 0
  );

  const totalEndpoints = categories.reduce((acc, cat) => acc + cat.endpoints.length, 0);
  const totalCategories = categories.length;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8 max-w-[1800px] mx-auto"
    >
      {/* Page Header */}
      <motion.header variants={itemVariants} className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-gpu-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                <Code2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-white">
                API Endpoints
              </h1>
            </div>
            <p className="text-sm sm:text-base text-surface-400 leading-relaxed">
              Complete reference of all available API endpoints in the admin dashboard
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right">
              <div className="text-2xl sm:text-3xl font-semibold text-white">{totalEndpoints}</div>
              <div className="text-xs sm:text-sm text-surface-400">Endpoints</div>
            </div>
            <div className="w-px h-10 sm:h-12 bg-surface-800" />
            <div className="text-right">
              <div className="text-2xl sm:text-3xl font-semibold text-white">{totalCategories}</div>
              <div className="text-xs sm:text-sm text-surface-400">Categories</div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Search and Filters */}
      <motion.div variants={itemVariants} className="flex gap-4 items-center flex-wrap">
        {/* Search Bar */}
        <div className="flex-1 min-w-0 sm:min-w-[300px] relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
          <input
            type="text"
            placeholder="Search endpoints by path, method, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-surface-900/80 border border-surface-800/60 rounded-xl text-surface-200 placeholder-surface-500 focus:outline-none focus:border-gpu-500/50 focus:ring-1 focus:ring-gpu-500/50 transition-all"
          />
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory(null)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all',
              !selectedCategory
                ? 'bg-gpu-500 text-white shadow-lg shadow-gpu-500/25'
                : 'bg-surface-800/50 text-surface-300 hover:bg-surface-800'
            )}
          >
            All Categories
          </button>
          {categories.slice(0, 3).map((category) => (
            <button
              key={category.name}
              onClick={() => setSelectedCategory(category.name === selectedCategory ? null : category.name)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
                selectedCategory === category.name
                  ? 'bg-gpu-500 text-white shadow-lg shadow-gpu-500/25'
                  : 'bg-surface-800/50 text-surface-300 hover:bg-surface-800'
              )}
            >
              <category.icon className="w-4 h-4" />
              {category.name}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Info Banner */}
      <motion.div
        variants={itemVariants}
        className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3"
      >
        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
          <Code2 className="w-5 h-5 text-blue-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-blue-400 mb-1">Base URL</h3>
          <code className="text-sm text-surface-300 bg-surface-900/50 px-3 py-1.5 rounded-lg inline-block font-mono">
            {window.location.origin}
          </code>
          <p className="text-sm text-surface-400 mt-2">
            All endpoints require authentication unless otherwise specified. Use JWT tokens for user authentication or API keys for programmatic access.
          </p>
        </div>
      </motion.div>

      {/* Endpoint Categories */}
      <motion.div variants={containerVariants} className="space-y-6">
        {filteredCategories.map((category) => (
          <motion.section
            key={category.name}
            variants={itemVariants}
            className="bg-surface-900/80 border border-surface-800/60 rounded-2xl overflow-hidden"
          >
            {/* Category Header */}
            <div className={`px-6 py-5 border-b border-surface-800/50 bg-gradient-to-br ${category.bgGradient}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-surface-900/50 flex items-center justify-center border border-surface-800/50">
                  <category.icon className={`w-5 h-5 ${category.color}`} />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-white">{category.name}</h2>
                  <p className="text-sm text-surface-400 mt-0.5">
                    {category.endpoints.length} endpoint{category.endpoints.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Endpoints List */}
            <div className="divide-y divide-surface-800/30">
              {category.endpoints.map((endpoint, idx) => (
                <div
                  key={`${endpoint.method}-${endpoint.path}-${idx}`}
                  className="px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* Method Badge */}
                    <span
                      className={clsx(
                        'inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold border font-mono flex-shrink-0',
                        getMethodColor(endpoint.method)
                      )}
                    >
                      {endpoint.method}
                    </span>

                    {/* Path and Description */}
                    <div className="flex-1 min-w-0">
                      <code className="text-sm font-mono text-surface-200 block mb-1 break-all">
                        {endpoint.path}
                      </code>
                      <p className="text-sm text-surface-400">{endpoint.description}</p>
                    </div>

                    {/* Auth Badge */}
                    {endpoint.auth && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-500/10 text-violet-400 border border-violet-500/20 flex-shrink-0">
                        <Shield className="w-3 h-3" />
                        {endpoint.auth}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        ))}

        {/* No Results */}
        {filteredCategories.length === 0 && (
          <motion.div
            variants={itemVariants}
            className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-12 text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-surface-800/50 flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-surface-600" />
            </div>
            <p className="text-surface-400 font-medium mb-2">No endpoints found</p>
            <p className="text-sm text-surface-500">
              Try adjusting your search or filters
            </p>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
